// Weekend schedule generation utilities
// Note: These functions have complex dependencies on state and other functions
// They may need to be refactored further or kept in the main component

import { getMonthsBetween, getWeekendsForMonth, getWeekStartForDate, getWeeksBetween } from '../scheduleGenerators/dateHelpers';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(`${dateStr}T00:00:00`);
  return isNaN(date.getTime()) ? null : date;
};

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

export const buildBlockRotationRanges = (rotationSchedules) => {
  const rangesByResident = {};

  Object.values(rotationSchedules).forEach((scheduleData) => {
    if (scheduleData?.scheduleType !== 'blocks') return;
    Object.entries(scheduleData.schedule || {}).forEach(([residentId, residentData]) => {
      if (!residentData.rotations) return;
      const weeksByRotation = {};
      Object.entries(residentData.rotations).forEach(([weekKey, rotationName]) => {
        if (!rotationName) return;
        if (!weeksByRotation[rotationName]) {
          weeksByRotation[rotationName] = [];
        }
        weeksByRotation[rotationName].push(weekKey);
      });

      Object.entries(weeksByRotation).forEach(([rotationName, weekKeys]) => {
        const sortedWeekDates = weekKeys
          .map(parseDate)
          .filter(Boolean)
          .sort((a, b) => a - b);
        if (sortedWeekDates.length === 0) return;

        const ranges = [];
        let rangeStart = sortedWeekDates[0];
        let prevWeek = sortedWeekDates[0];

        for (let i = 1; i < sortedWeekDates.length; i += 1) {
          const currentWeek = sortedWeekDates[i];
          const diff = currentWeek.getTime() - prevWeek.getTime();
          if (diff > (7 * DAY_IN_MS + (12 * 60 * 60 * 1000))) {
            ranges.push({ start: rangeStart, end: addDays(prevWeek, 6) });
            rangeStart = currentWeek;
          }
          prevWeek = currentWeek;
        }
        ranges.push({ start: rangeStart, end: addDays(prevWeek, 6) });

        if (!rangesByResident[residentId]) {
          rangesByResident[residentId] = {};
        }
        rangesByResident[residentId][rotationName] = ranges;
      });
    });
  });

  return rangesByResident;
};

export const weekendFallsWithinBlockRange = (ranges = [], weekend) => {
  if (!ranges || ranges.length === 0) return false;
  const dates = [];
  if (weekend?.saturday) {
    const sat = parseDate(weekend.saturday);
    if (sat) dates.push(sat);
  }
  if (weekend?.sunday) {
    const sun = parseDate(weekend.sunday);
    if (sun) dates.push(sun);
  }
  if (dates.length === 0) return false;
  return ranges.some(({ start, end }) => (
    dates.some((date) => date >= start && date <= end)
  ));
};

/**
 * Generate weekend schedules from rotation schedules
 * @param {Object} rotationSchedules - Rotation schedules by PGY level
 * @param {Array} offServiceResidents - Array of off-service resident objects
 * @returns {Object} Combined weekend schedules by rotation
 */
export const generateAndStoreWeekendSchedules = (rotationSchedules, offServiceResidents = []) => {
  const combinedSchedules = {};
  const blockRotationRanges = buildBlockRotationRanges(rotationSchedules);
  
  // Build block definitions for each schedule to determine which block a weekend falls into
  const blockDefinitionsBySchedule = {};
  Object.entries(rotationSchedules).forEach(([pgyLevel, scheduleData]) => {
    if (scheduleData?.scheduleType === 'blocks' && scheduleData?.startDate && scheduleData?.endDate) {
      const pattern = scheduleData.blockPattern || "4-1";
      const [majorWeeksCountRaw, minorWeeksCountRaw] = pattern
        .split("-")
        .map((val) => parseInt(val, 10) || 0);
      const majorWeeksCount = Math.max(0, majorWeeksCountRaw);
      const minorWeeksCount = Math.max(0, minorWeeksCountRaw);
      const defaultDuration = majorWeeksCount + minorWeeksCount || 5;
      const blockDurationValue = scheduleData.blockDuration || defaultDuration || 5;

      const weeks = getWeeksBetween(scheduleData.startDate, scheduleData.endDate);
      const blocks = [];

      for (let i = 0; i < weeks.length; i += blockDurationValue) {
        const blockWeeks = weeks.slice(i, i + blockDurationValue);
        if (!blockWeeks.length) continue;

        const blockIndex = blocks.length + 1;
        const startDate = new Date(blockWeeks[0].start);
        const endDate = new Date(blockWeeks[blockWeeks.length - 1].end);

        blocks.push({
          index: blockIndex,
          startDate,
          endDate,
          majorWeeks: blockWeeks.slice(0, majorWeeksCount),
          minorWeeks: blockWeeks.slice(majorWeeksCount, majorWeeksCount + minorWeeksCount),
        });
      }

      blockDefinitionsBySchedule[pgyLevel] = blocks;
    }
  });

  // Helper function to determine which block a weekend falls into for a given schedule
  const getBlockIndexForWeekend = (weekend, scheduleData, pgyLevel) => {
    if (scheduleData?.scheduleType !== 'blocks') return null;
    const blocks = blockDefinitionsBySchedule[pgyLevel];
    if (!blocks || !blocks.length) return null;

    const saturdayDate = parseDate(weekend.saturday);
    const sundayDate = weekend.sunday ? parseDate(weekend.sunday) : null;
    
    // Check if either Saturday or Sunday falls within a block
    for (const block of blocks) {
      const saturdayInBlock = saturdayDate && saturdayDate >= block.startDate && saturdayDate <= block.endDate;
      const sundayInBlock = sundayDate && sundayDate >= block.startDate && sundayDate <= block.endDate;
      
      if (saturdayInBlock || sundayInBlock) {
        return block.index;
      }
    }
    return null;
  };
  
  // Collect time periods month-wise for weekend grouping (weekend UI is month-based)
  const allMonths = new Set();
  Object.values(rotationSchedules).forEach((scheduleData) => {
    if (!scheduleData?.startDate || !scheduleData?.endDate) return;
    const months = getMonthsBetween(scheduleData.startDate, scheduleData.endDate);
    months.forEach((m) => allMonths.add(m));
  });
  const months = Array.from(allMonths).sort();

  // Get all rotations including those from off-service residents
  const allRotationsFromOffService = new Set();
  offServiceResidents.forEach(osr => {
    if (osr.rotationName) {
      allRotationsFromOffService.add(osr.rotationName.trim());
    }
    // Also add major and minor rotations for block schedules
    if (osr.scheduleType === 'blocks') {
      if (osr.majorRotation) {
        allRotationsFromOffService.add(osr.majorRotation.trim());
      }
      if (osr.minorRotation) {
        allRotationsFromOffService.add(osr.minorRotation.trim());
      }
    }
  });

  // Get all unique rotations across all PGY levels
  const allRotations = new Set();
  Object.values(rotationSchedules).forEach((scheduleData) => {
    Object.values(scheduleData.schedule || {}).forEach((residentData) => {
      Object.values(residentData.rotations || {}).forEach((rotation) => {
        if (rotation) allRotations.add(rotation);
      });
    });
  });

  // Add rotations from off-service residents
  allRotationsFromOffService.forEach(rot => allRotations.add(rot));

  // For each rotation, for each month, generate weekends and assign residents
  Array.from(allRotations).forEach((rotation) => {
    combinedSchedules[rotation] = {};
    months.forEach((month) => {
      const weekends = getWeekendsForMonth(month);
      
      weekends.forEach((weekend) => {
        // Build rotationResidents for this specific weekend
        const rotationResidents = [];
        Object.values(rotationSchedules).forEach((scheduleData) => {
          Object.entries(scheduleData.schedule || {}).forEach(([residentId, residentData]) => {
            if (!residentData.rotations) return;
            if (scheduleData.scheduleType === 'weekly' || scheduleData.scheduleType === 'blocks') {
              const weekKey = getWeekStartForDate(weekend.saturday);
              if (residentData.rotations[weekKey] === rotation) {
                if (scheduleData.scheduleType === 'blocks') {
                  const ranges = blockRotationRanges?.[residentId]?.[rotation];
                  if (ranges && !weekendFallsWithinBlockRange(ranges, weekend)) {
                    return;
                  }
                }
                rotationResidents.push({ id: residentId, name: residentData.name, year: parseInt(residentData.year) });
              }
            } else {
              if (residentData.rotations[month] === rotation) {
                rotationResidents.push({ id: residentId, name: residentData.name, year: parseInt(residentData.year) });
              }
            }
          });
        });

        // Add off-service residents for this rotation and month
        offServiceResidents.forEach(osr => {
          const normalizedRotation = (rotation || '').trim();
          let shouldInclude = false;
          let rotationDateRange = null; // Store the date range for this rotation

          // Handle block schedules
          if (osr.scheduleType === 'blocks') {
            const osrBlockIndex = osr.blockIndex ? parseInt(osr.blockIndex, 10) : null;
            const osrMajorRotation = (osr.majorRotation || '').trim();
            const osrMinorRotation = (osr.minorRotation || '').trim();

            // Check if rotation matches major or minor rotation
            const isMajorRotation = osrMajorRotation && osrMajorRotation.toLowerCase() === normalizedRotation.toLowerCase();
            const isMinorRotation = osrMinorRotation && osrMinorRotation.toLowerCase() === normalizedRotation.toLowerCase();
            const rotationMatches = isMajorRotation || isMinorRotation;

            if (rotationMatches && osrBlockIndex) {
              // Find which schedule this off-service resident belongs to (by PGY level)
              Object.entries(rotationSchedules).forEach(([pgyLevel, scheduleData]) => {
                // Match by PGY level (pgyLevel key should match osr.pgyLevel)
                if (scheduleData?.scheduleType === 'blocks' && 
                    pgyLevel?.toString() === osr.pgyLevel?.toString()) {
                  const blockIndexForWeekend = getBlockIndexForWeekend(weekend, scheduleData, pgyLevel);
                  if (blockIndexForWeekend === osrBlockIndex) {
                    // Get the block definition to determine rotation date range
                    const blocks = blockDefinitionsBySchedule[pgyLevel];
                    if (blocks && blocks.length >= osrBlockIndex) {
                      const block = blocks[osrBlockIndex - 1]; // blockIndex is 1-based
                      if (block) {
                        // Determine date range based on whether it's major or minor rotation
                        if (isMajorRotation && block.majorWeeks && block.majorWeeks.length > 0) {
                          const firstWeek = block.majorWeeks[0];
                          const lastWeek = block.majorWeeks[block.majorWeeks.length - 1];
                          rotationDateRange = {
                            start: new Date(firstWeek.start),
                            end: new Date(lastWeek.end)
                          };
                        } else if (isMinorRotation && block.minorWeeks && block.minorWeeks.length > 0) {
                          const firstWeek = block.minorWeeks[0];
                          const lastWeek = block.minorWeeks[block.minorWeeks.length - 1];
                          rotationDateRange = {
                            start: new Date(firstWeek.start),
                            end: new Date(lastWeek.end)
                          };
                        }
                        
                        // Check if weekend falls within the rotation date range
                        const weekendDate = parseDate(weekend.saturday);
                        if (weekendDate && rotationDateRange) {
                          if (weekendDate >= rotationDateRange.start && weekendDate <= rotationDateRange.end) {
                            shouldInclude = true;
                          }
                        } else if (!rotationDateRange) {
                          // Fallback: if we can't determine the date range, just check block index
                          shouldInclude = true;
                        }
                      }
                    }
                  }
                }
              });
            }
          } else {
            // Handle monthly and weekly schedules
            const osrMonth = (osr.month || '').trim();
            const osrRotation = (osr.rotationName || '').trim();
            
            // For monthly schedules, check if month matches
            // For weekly schedules, check if the weekend's month matches
            const weekendMonth = weekend.saturday.substring(0, 7); // YYYY-MM from YYYY-MM-DD
            
            if (osrRotation.toLowerCase() === normalizedRotation.toLowerCase() && 
                (osrMonth === month || osrMonth === weekendMonth)) {
              shouldInclude = true;
            }
          }

          if (shouldInclude) {
            // Use a special identifier for off-service residents (negative ID or special format)
            rotationResidents.push({ 
              id: `off-service-${osr.id}`, 
              name: osr.residentName, 
              year: osr.pgyLevel,
              isOffService: true,
              rotationDateRange: rotationDateRange // Store date range for golden weekend logic
            });
          }
        });

        // Group residents by PGY level for this weekend
        const residentsByPGY = {};
        rotationResidents.forEach(resident => {
          if (!residentsByPGY[resident.year]) {
            residentsByPGY[resident.year] = [];
          }
          residentsByPGY[resident.year].push(resident);
        });

        // Gather all residents for this weekend
        const allResidents = [];
        [1, 2, 3, 4, 5].forEach(pgy => {
          if (residentsByPGY[pgy]) {
            allResidents.push(...residentsByPGY[pgy]);
          }
        });

        // Shuffle helper
        const shuffle = (arr) => arr.sort(() => Math.random() - 0.5);
        const randomDay = () => (Math.random() < 0.5 ? 'sat' : 'sun');

        let saturdayResidents = [];
        let sundayResidents = [];

        // --- Custom PGY assignment rules ---
        const pgySet = new Set(allResidents.map(r => r.year));
        // 1. Only PGY 4 & 5, or 4/5/3, or 2/3/4, or 2/3/5
        if (
          (pgySet.size === allResidents.length) && (
            (pgySet.has(4) && pgySet.has(5) && allResidents.length <= 3) ||
            (pgySet.has(4) && pgySet.has(5) && pgySet.has(3) && allResidents.length === 3) ||
            (pgySet.has(2) && pgySet.has(3) && pgySet.has(4) && allResidents.length === 3) ||
            (pgySet.has(2) && pgySet.has(3) && pgySet.has(5) && allResidents.length === 3)
          )
        ) {
          const shuffled = shuffle([...allResidents]);
          if (allResidents.length === 3) {
            // Max 2 per day
            if (randomDay() === 'sat') {
              saturdayResidents = shuffled.slice(0, 2);
              sundayResidents = shuffled.slice(2);
            } else {
              saturdayResidents = shuffled.slice(0, 1);
              sundayResidents = shuffled.slice(1);
            }
          } else if (allResidents.length === 2) {
            if (randomDay() === 'sat') {
              saturdayResidents = [shuffled[0]];
              sundayResidents = [shuffled[1]];
            } else {
              saturdayResidents = [shuffled[1]];
              sundayResidents = [shuffled[0]];
            }
          }
          combinedSchedules[rotation][weekend.saturday] = {
            date: weekend.saturday,
            residents: saturdayResidents,
            type: "Saturday",
          };
          combinedSchedules[rotation][weekend.sunday] = {
            date: weekend.sunday,
            residents: sundayResidents,
            type: "Sunday",
          };
          return; // skip rest of logic for this weekend
        }

        // 2. If PGY 1,2,3: ensure PGY 1 is paired with 2 or 3
        if (pgySet.size === 3 && pgySet.has(1) && pgySet.has(2) && pgySet.has(3) && allResidents.length === 3) {
          const pgy1 = allResidents.find(r => r.year === 1);
          const pgy2 = allResidents.find(r => r.year === 2);
          const pgy3 = allResidents.find(r => r.year === 3);
          if (randomDay() === 'sat') {
            if (Math.random() < 0.5) {
              saturdayResidents = [pgy1, pgy2];
              sundayResidents = [pgy3];
            } else {
              saturdayResidents = [pgy1, pgy3];
              sundayResidents = [pgy2];
            }
          } else {
            if (Math.random() < 0.5) {
              sundayResidents = [pgy1, pgy2];
              saturdayResidents = [pgy3];
            } else {
              sundayResidents = [pgy1, pgy3];
              saturdayResidents = [pgy2];
            }
          }
          combinedSchedules[rotation][weekend.saturday] = {
            date: weekend.saturday,
            residents: saturdayResidents,
            type: "Saturday",
          };
          combinedSchedules[rotation][weekend.sunday] = {
            date: weekend.sunday,
            residents: sundayResidents,
            type: "Sunday",
          };
          return;
        }

        // 3. Only 1 resident
        if (allResidents.length === 1) {
          if (randomDay() === 'sat') {
            saturdayResidents = [allResidents[0]];
            sundayResidents = [];
          } else {
            saturdayResidents = [];
            sundayResidents = [allResidents[0]];
          }
          combinedSchedules[rotation][weekend.saturday] = {
            date: weekend.saturday,
            residents: saturdayResidents,
            type: "Saturday",
          };
          combinedSchedules[rotation][weekend.sunday] = {
            date: weekend.sunday,
            residents: sundayResidents,
            type: "Sunday",
          };
          return;
        }

        // If there are 5 residents, assign 2 to one day, 3 to the other (randomly)
        if (allResidents.length === 5) {
          const shuffled = shuffle([...allResidents]);
          if (Math.random() < 0.5) {
            saturdayResidents = shuffled.slice(0, 2);
            sundayResidents = shuffled.slice(2);
          } else {
            saturdayResidents = shuffled.slice(0, 3);
            sundayResidents = shuffled.slice(3);
          }
        } else if (allResidents.length === 3) {
          // New rule: If there are only 3 residents, pair higher PGY level (4 or 5) with lower PGY level (1)
          // and if there's one PGY 4 or 5, allow pairing of PGY 1 and 2
          
          // Check if we have PGY 4 or 5 residents
          const highPGYResidents = allResidents.filter(r => r.year === 4 || r.year === 5);
          const lowPGYResidents = allResidents.filter(r => r.year === 1 || r.year === 2);
          const midPGYResidents = allResidents.filter(r => r.year === 3);
          
          if (highPGYResidents.length > 0 && lowPGYResidents.length > 0) {
            // We have both high and low PGY residents
            const highPGY = shuffle([...highPGYResidents])[0];
            const lowPGY = shuffle([...lowPGYResidents])[0];
            const remaining = allResidents.filter(r => r.id !== highPGY.id && r.id !== lowPGY.id)[0];
            
            // Assign high PGY with low PGY on one day, remaining on other day
            if (Math.random() < 0.5) {
              saturdayResidents = [highPGY, lowPGY];
              sundayResidents = [remaining];
            } else {
              saturdayResidents = [remaining];
              sundayResidents = [highPGY, lowPGY];
            }
          } else if (highPGYResidents.length === 1 && lowPGYResidents.length >= 2) {
            // We have exactly one high PGY and multiple low PGY - allow pairing of PGY 1 and 2
            const highPGY = highPGYResidents[0];
            const pgy1Residents = lowPGYResidents.filter(r => r.year === 1);
            const pgy2Residents = lowPGYResidents.filter(r => r.year === 2);
            
            if (pgy1Residents.length > 0 && pgy2Residents.length > 0) {
              // We can pair PGY 1 and 2
              const pgy1 = shuffle([...pgy1Residents])[0];
              const pgy2 = shuffle([...pgy2Residents])[0];
              
              if (Math.random() < 0.5) {
                saturdayResidents = [highPGY, pgy1];
                sundayResidents = [pgy2];
              } else {
                saturdayResidents = [pgy2];
                sundayResidents = [highPGY, pgy1];
              }
            } else {
              // Fallback to random assignment
              const shuffled = shuffle([...allResidents]);
              saturdayResidents = shuffled.slice(0, 2);
              sundayResidents = shuffled.slice(2);
            }
          } else {
            // Fallback to random assignment for other 3-resident scenarios
            const shuffled = shuffle([...allResidents]);
            saturdayResidents = shuffled.slice(0, 2);
            sundayResidents = shuffled.slice(2);
          }
        } else if ((residentsByPGY[1] && residentsByPGY[1].length > 0) || (residentsByPGY[2] && residentsByPGY[2].length > 0)) {
          // If PGY-1 or PGY-2 present, use strict assignment
          // 1. Assign ALL PGY-1 residents randomly to Sat/Sun
          let pgy1Assigned = false;
          if (residentsByPGY[1] && residentsByPGY[1].length > 0) {
            const pgy1Residents = shuffle([...residentsByPGY[1]]);
            
            // Distribute PGY-1 residents randomly between Saturday and Sunday
            pgy1Residents.forEach((pgy1Resident, index) => {
              if (index === 0) {
                // First PGY-1 determines the primary day
                if (Math.random() < 0.5) {
                  saturdayResidents.push(pgy1Resident);
                  pgy1Assigned = 'sat';
                } else {
                  sundayResidents.push(pgy1Resident);
                  pgy1Assigned = 'sun';
                }
              } else {
                // Additional PGY-1 residents are randomly assigned
                if (Math.random() < 0.5) {
                  saturdayResidents.push(pgy1Resident);
                } else {
                  sundayResidents.push(pgy1Resident);
                }
              }
            });
          }
          // 2. Assign ALL PGY-2 residents randomly
          if (residentsByPGY[2] && residentsByPGY[2].length > 0) {
            const pgy2Residents = shuffle([...residentsByPGY[2]]);
            
            // Distribute PGY-2 residents randomly
            pgy2Residents.forEach((pgy2Resident, index) => {
              if (index === 0 && pgy1Assigned) {
                // First PGY-2 goes to opposite day of PGY-1 if PGY-1 was assigned
                if (pgy1Assigned === 'sat') {
                  sundayResidents.push(pgy2Resident);
                } else if (pgy1Assigned === 'sun') {
                  saturdayResidents.push(pgy2Resident);
                }
              } else {
                // Additional PGY-2 residents are randomly assigned
                if (Math.random() < 0.5) {
                  saturdayResidents.push(pgy2Resident);
                } else {
                  sundayResidents.push(pgy2Resident);
                }
              }
            });
          }
          // 3. Assign remaining (PGY-3,4,5) with preference, but strictly fill up to 2 per day
          const remainingResidents = [];
          [3, 4, 5].forEach(pgy => {
            if (residentsByPGY[pgy]) {
              remainingResidents.push(...residentsByPGY[pgy]);
            }
          });
          const shuffledRemaining = shuffle([...remainingResidents]);
          shuffledRemaining.forEach(resident => {
            // Prefer to pair PGY-5/4/3 with PGY-1, and PGY-3/4 with PGY-2, but strictly fill up to 2 per day
            if (saturdayResidents.length < 2 && sundayResidents.length < 2) {
              // If both days have space, assign by preference
              if (pgy1Assigned === 'sat' && (resident.year === 5 || resident.year === 4 || resident.year === 3)) {
                if (sundayResidents.length < 2) {
                  sundayResidents.push(resident);
                  return;
                }
              }
              if (pgy1Assigned === 'sun' && (resident.year === 5 || resident.year === 4 || resident.year === 3)) {
                if (saturdayResidents.length < 2) {
                  saturdayResidents.push(resident);
                  return;
                }
              }
              // If PGY-2 is on Sunday, prefer PGY-3/4 for Saturday
              if (sundayResidents.some(r => r.year === 2) && (resident.year === 3 || resident.year === 4)) {
                if (saturdayResidents.length < 2) {
                  saturdayResidents.push(resident);
                  return;
                }
              }
              // If PGY-2 is on Saturday, prefer PGY-3/4 for Sunday
              if (saturdayResidents.some(r => r.year === 2) && (resident.year === 3 || resident.year === 4)) {
                if (sundayResidents.length < 2) {
                  sundayResidents.push(resident);
                  return;
                }
              }
            }
            // Otherwise, fill up to 2 per day
            if (saturdayResidents.length < 2) {
              saturdayResidents.push(resident);
            } else if (sundayResidents.length < 2) {
              sundayResidents.push(resident);
            }
          });
        } else {
          // No PGY-1 or PGY-2, assign all randomly to Sat/Sun, up to 2 per day
          const shuffled = shuffle([...allResidents]);
          shuffled.forEach(resident => {
            if (saturdayResidents.length < 2) {
              saturdayResidents.push(resident);
            } else if (sundayResidents.length < 2) {
              sundayResidents.push(resident);
            }
          });
        }

        // Save the assignments
        combinedSchedules[rotation][weekend.saturday] = {
          date: weekend.saturday,
          residents: saturdayResidents,
          type: "Saturday",
        };
        combinedSchedules[rotation][weekend.sunday] = {
          date: weekend.sunday,
          residents: sundayResidents,
          type: "Sunday",
        };
      });
    });
  });

  return combinedSchedules;
};

// Note: generateWeekendSchedulesWithOptions is very large and complex
// It includes gold-weekend mode logic that may need to remain in the main component
// or be extracted in a separate file due to its size and complexity

